import React from "react";
import { Formik, Field, Form } from "formik";
import classNames from "classnames";
import { makeStyles } from "@material-ui/styles";
import { Button } from "@mantine/core";
import MuiTextInput from "../../Common/MuiTextInput";
import { getFieldInfo, validate } from "./util";

const useStyles = makeStyles((theme) => ({
  fieldsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2,1fr)",
    gridGap: "16px",
    paddingTop: "16px",
    "@media(max-width: 667px)": {
      gridTemplateColumns: "1fr",
    },
  },
  actionContainer: {
    marginTop: "16px",
    borderTop: `1px solid ${theme.app.borderColor}`,
    padding: "8px 0",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
  },
  formActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    "& button": {
      marginLeft: "8px",
    },
  },
  button: { color: theme.app.labelColor },
  backButton: { marginRight: "8px", color: theme.app.labelColor },
}));

const MemberForm = (props) => {
  const { initialValues, language, fieldInfo, ticket, handleSubmit } = props;
  const classes = useStyles();
  const { visibleFields, requiredFields } = getFieldInfo(fieldInfo, ticket);

  const validateCellPhone = (value) => {
    const regexPattern = /^(\+(\d{1,3}\s*)|)\d{1,4}(-|\/|\s|)(\d\s*){5,10}$/;

    if (requiredFields.includes("cellPhone") && !value) {
      return (
        (language.MemberForm && language.MemberForm.CellPhone_Required) ||
        "Telefonnummer är obligatoriskt"
      );
    }

    if (value.length > 15) {
      return language.MemberForm.Invalid_Length
        ? language.MemberForm.Invalid_Length
        : "Ogiltig längd";
    } else if (value.length > 0 && !regexPattern.test(value)) {
      return language.CellPhone_Error
        ? language.CellPhone_Error
        : "Ogiltigt telefonnummer";
    }
  };

  if (!language.MemberForm) return <div />;
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, actions) => handleSubmit(values, actions)}
      validate={(values) =>
        validate(values, language, ticket, requiredFields, visibleFields)
      }
      render={({ isValid, isSubmitting }) => (
        <Form>
          <div className={classes.fieldsContainer}>
            {visibleFields.includes("firstname") && (
              <Field
                component={MuiTextInput}
                name="firstname"
                label={`${
                  language.MemberForm && language.MemberForm.FirstName_Label
                }`}
                placeholder={`${
                  language.MemberForm &&
                  language.MemberForm.FirstName_Placeholder
                }`}
                fullWidth={true}
                type="text"
                required={requiredFields.includes("firstname")}
                autocomplete="given-name"
              />
            )}
            {visibleFields.includes("lastname") && (
              <Field
                component={MuiTextInput}
                name="lastname"
                label={`${
                  language.MemberForm && language.MemberForm.LastName_Label
                }`}
                placeholder={`${
                  language.MemberForm &&
                  language.MemberForm.LastName_Placeholder
                }`}
                fullWidth={true}
                type="text"
                required={requiredFields.includes("lastname")}
                autocomplete="family-name"
              />
            )}
            {visibleFields.includes("civicRegistrationNumber") && (
              <Field
                component={MuiTextInput}
                name="civicRegistrationNumber"
                label={`${
                  language.MemberForm &&
                  language.MemberForm.CivicRegistration_Label
                }`}
                placeholder={`${
                  language.MemberForm &&
                  language.MemberForm.CivicRegistration_Placeholder
                }`}
                fullWidth={true}
                type="text"
                required={requiredFields.includes("civicRegistrationNumber")}
              />
            )}
            {visibleFields.includes("email") && (
              <Field
                component={MuiTextInput}
                name="email"
                label={`${
                  language.MemberForm && language.MemberForm.Email_Label
                }`}
                placeholder={`${
                  language.MemberForm && language.MemberForm.Email_Placeholder
                }`}
                fullWidth={true}
                type="text"
                required={requiredFields.includes("email")}
                autocomplete="email"
              />
            )}
            {visibleFields.includes("zipCode") && (
              <Field
                component={MuiTextInput}
                name="zipCode"
                label={`${
                  language.MemberForm && language.MemberForm.ZipCode_Label
                }`}
                placeholder={`${
                  language.MemberForm && language.MemberForm.ZipCode_Placeholder
                }`}
                fullWidth={true}
                type="string"
                required={requiredFields.includes("zipCode")}
                autocomplete="postal-code"
              />
            )}
            {visibleFields.includes("cellPhone") && (
              <Field
                component={MuiTextInput}
                name="cellPhone"
                label={`${
                  language.MemberForm && language.MemberForm.CellPhone_Label
                }`}
                placeholder={`${
                  language.MemberForm &&
                  language.MemberForm.CellPhone_Placeholder
                }`}
                fullWidth={true}
                validate={validateCellPhone}
                type="string"
                required={requiredFields.includes("cellPhone")}
                autocomplete="tel"
              />
            )}
            {ticket && (
              <Field
                component={MuiTextInput}
                name="id"
                label={`${
                  language.MemberForm && language.MemberForm.TicketId_Label
                }`}
                placeholder={`${
                  language.MemberForm &&
                  language.MemberForm.TicketId_Placeholder
                }`}
                type="text"
                fullWidth={true}
                required={true}
              />
            )}
          </div>
          <div className={classes.actionContainer}>
            <div>
              <Button
                className={classNames(
                  classes.backButton,
                  "members__form__back-button"
                )}
                onClick={() => window.history.back()}
                variant="subtle"
                color="dark"
              >
                {`${language && language.Back}`}
              </Button>
            </div>
            <div className={classes.formActions}>
              <Button
                type="submit"
                disabled={!isValid || isSubmitting}
                color="primary"
                className="members__form__submit-button primaryButton"
              >
                {`${language && language.Register}`}
              </Button>
            </div>
          </div>
        </Form>
      )}
    />
  );
};

export default MemberForm;
